// AGC: CropSuggestions - Suggest crops based on region and date
// AGC: [CROP_REC] Now uses geo + climate aware recommendations from backend API
// AGC: Falls back to mock recommendations if API unavailable
// AGC: Shows field info and allows crop selection
// =============================================================================
// AGC: [SOURCE_OF_TRUTH] This component uses:
// AGC:   - useCropRecommendations hook → backend recommendationService.js
// AGC:   - Canonical: recommendCropsForLot() from services/lotServices.ts
// AGC:   - Fallback: ALL_CROP_RECOMMENDATIONS (local mock) when API unavailable
// =============================================================================

import React, { useState, useMemo, useEffect } from 'react'
import { Check, Leaf, Calendar, MapPin, Ruler, Info, Pencil, X, RefreshCw, ThermometerSun, CloudRain, AlertTriangle, TrendingUp } from 'lucide-react'
import { TopBar, GlassCard, Button } from '../shared'
import { WizardLotProgressBar } from '../wizard/WizardLotProgressBar'
import { formatAreaHa, formatPerimeterM, calculatePerimeter } from '../../utils/geometry'
import { useCropRecommendations } from '../../hooks/useCropRecommendations'
import { useTranslation } from '../../hooks/useTranslation'
import type { Manejo } from '../../types'
import type { Screen, PlotFormData, CropType, CropRecommendation as APICropRecommendation } from '../../types'

// =============================================================================
// AGC: Loading step icon map — text comes from i18n keys fields.cropLoadingStep1..9
// =============================================================================
const LOADING_ICONS = ['📍', '🌤️', '🌧️', '🌡️', '☀️', '📅', '🌱', '📊', '✨']

// AGC: [AR] display names/icons for the pampean crops the Argentina engine returns by id.
const AR_CROP_NAMES: Record<string, { name: string; icon: string }> = {
  soja: { name: 'Soja', icon: '🫘' },
  maiz: { name: 'Maíz', icon: '🌽' },
  trigo: { name: 'Trigo', icon: '🌾' },
  girasol: { name: 'Girasol', icon: '🌻' },
  algodon: { name: 'Algodón', icon: '☁️' },
}

interface CropSuggestionsProps {
  plotData: PlotFormData
  onNavigate: (screen: Screen, data?: { plotData?: PlotFormData }) => void
  onBack: () => void
  // AGC: Pre-filled crop type from voice command (optional)
  voicePrefillCrop?: string
  // AGC: Wizard mode props
  wizardMode?: boolean
  wizardStep?: number
}

/**
 * AGC: [CROP_REC] Crop recommendation (extended with API fields)
 */
interface CropRecommendation {
  cropId: CropType
  name: string
  source: 'EMBRAPA' | 'INTA' | string
  icon: string
  suitability: 'excelente' | 'bom' | 'moderado'
  plantingWindow: string
  expectedYield: string
  notes: string
  // AGC: [CROP_REC] API-provided fields (optional for mock data)
  suitabilityScore?: number
  climateScore?: number
  seasonScore?: number
  risks?: string[]
  scores?: {
    precipitation: number
    temperature: number
    radiation: number
    season: number
  }
}

/**
 * AGC: Complete Brazilian crop database with EMBRAPA recommendations
 * AGC: Includes planting windows, yields, and management notes
 */
const ALL_CROP_RECOMMENDATIONS: CropRecommendation[] = [
  // ========================================
  // AGC: GRÃOS PRINCIPAIS (Main Grains)
  // ========================================
  {
    cropId: 'soja',
    name: 'Soja',
    source: 'EMBRAPA',
    icon: '🫘',
    suitability: 'excelente',
    plantingWindow: 'Setembro a Dezembro',
    expectedYield: '3.500 - 4.200 kg/ha (58-70 sc/ha)',
    notes: 'Principal cultura do Brasil. Ciclo 90-150 dias. Fixação biológica de nitrogênio. Rotação ideal com milho safrinha.',
  },
  {
    cropId: 'milho',
    name: 'Milho 1ª Safra',
    source: 'EMBRAPA',
    icon: '🌽',
    suitability: 'excelente',
    plantingWindow: 'Setembro a Novembro',
    expectedYield: '10.000 - 14.000 kg/ha (167-233 sc/ha)',
    notes: 'Híbridos de alta tecnologia. Ciclo 120-150 dias. Alta demanda de nitrogênio. Ideal para silagem ou grãos.',
  },
  {
    cropId: 'milho_safrinha',
    name: 'Milho 2ª Safra (Safrinha)',
    source: 'EMBRAPA',
    icon: '🌽',
    suitability: 'bom',
    plantingWindow: 'Janeiro a Março',
    expectedYield: '5.500 - 8.000 kg/ha (92-133 sc/ha)',
    notes: 'Após soja. Janela crítica até 10/março. Híbridos precoces e superprecoces. Risco de geada e seca.',
  },
  {
    cropId: 'trigo',
    name: 'Trigo',
    source: 'EMBRAPA',
    icon: '🌾',
    suitability: 'excelente',
    plantingWindow: 'Abril a Julho',
    expectedYield: '3.000 - 4.500 kg/ha (50-75 sc/ha)',
    notes: 'Cultura de inverno. Ciclo 110-150 dias. Atenção à ferrugem e giberela. Qualidade de panificação.',
  },
  {
    cropId: 'arroz',
    name: 'Arroz Irrigado',
    source: 'EMBRAPA',
    icon: '🍚',
    suitability: 'excelente',
    plantingWindow: 'Outubro a Dezembro',
    expectedYield: '7.000 - 10.000 kg/ha (117-167 sc/ha)',
    notes: 'Requer irrigação por inundação. RS e SC principais produtores. Ciclo 100-150 dias.',
  },
  {
    cropId: 'feijao',
    name: 'Feijão 1ª Safra (Águas)',
    source: 'EMBRAPA',
    icon: '🫘',
    suitability: 'bom',
    plantingWindow: 'Setembro a Novembro',
    expectedYield: '1.800 - 3.000 kg/ha (30-50 sc/ha)',
    notes: 'Ciclo curto 70-100 dias. Sensível a estresse hídrico. 3 safras possíveis/ano.',
  },
  {
    cropId: 'feijao_carioca',
    name: 'Feijão Carioca',
    source: 'EMBRAPA',
    icon: '🫘',
    suitability: 'bom',
    plantingWindow: 'Fev-Mar (seca) ou Set-Nov (águas)',
    expectedYield: '2.000 - 3.200 kg/ha (33-53 sc/ha)',
    notes: 'Tipo mais consumido no Brasil. Exigente em fertilidade. Sensível a doenças foliares.',
  },
  {
    cropId: 'feijao_preto',
    name: 'Feijão Preto',
    source: 'EMBRAPA',
    icon: '🫘',
    suitability: 'bom',
    plantingWindow: 'Fev-Mar (seca) ou Set-Nov (águas)',
    expectedYield: '1.800 - 2.800 kg/ha (30-47 sc/ha)',
    notes: 'Preferência no Sul e RJ. Mais tolerante a condições adversas que carioca.',
  },
  // ========================================
  // AGC: OUTROS GRÃOS (Other Grains)
  // ========================================
  {
    cropId: 'sorgo',
    name: 'Sorgo Granífero',
    source: 'EMBRAPA',
    icon: '🌾',
    suitability: 'bom',
    plantingWindow: 'Fevereiro a Março (safrinha)',
    expectedYield: '3.500 - 6.000 kg/ha',
    notes: 'Tolerante à seca. Alternativa ao milho safrinha. Usado para ração animal.',
  },
  {
    cropId: 'aveia',
    name: 'Aveia Branca',
    source: 'EMBRAPA',
    icon: '🌾',
    suitability: 'bom',
    plantingWindow: 'Abril a Junho',
    expectedYield: '2.500 - 4.000 kg/ha',
    notes: 'Cultura de inverno. Duplo propósito: grãos e pastejo. Cobertura de solo.',
  },
  {
    cropId: 'cevada',
    name: 'Cevada Cervejeira',
    source: 'EMBRAPA',
    icon: '🌾',
    suitability: 'moderado',
    plantingWindow: 'Maio a Julho',
    expectedYield: '3.000 - 4.500 kg/ha',
    notes: 'Exigente em qualidade. Contrato com maltarias. Sul do Brasil principalmente.',
  },
  {
    cropId: 'centeio',
    name: 'Centeio',
    source: 'EMBRAPA',
    icon: '🌾',
    suitability: 'moderado',
    plantingWindow: 'Abril a Junho',
    expectedYield: '2.000 - 3.500 kg/ha',
    notes: 'Tolerante ao frio. Cobertura de solo e forragem. Panificação especial.',
  },
  {
    cropId: 'triticale',
    name: 'Triticale',
    source: 'EMBRAPA',
    icon: '🌾',
    suitability: 'bom',
    plantingWindow: 'Abril a Junho',
    expectedYield: '3.500 - 5.500 kg/ha',
    notes: 'Híbrido trigo x centeio. Forragem e grãos. Rústico e produtivo.',
  },
  {
    cropId: 'milheto',
    name: 'Milheto',
    source: 'EMBRAPA',
    icon: '🌾',
    suitability: 'bom',
    plantingWindow: 'Setembro a Março',
    expectedYield: '1.500 - 3.000 kg/ha (grãos)',
    notes: 'Cobertura e pastejo. Tolerante à seca. Sistema radicular profundo.',
  },
  // ========================================
  // AGC: OLEAGINOSAS (Oilseeds)
  // ========================================
  {
    cropId: 'algodao',
    name: 'Algodão',
    source: 'EMBRAPA',
    icon: '☁️',
    suitability: 'bom',
    plantingWindow: 'Novembro a Janeiro',
    expectedYield: '280 - 380 @/ha (pluma)',
    notes: 'Alta tecnologia. Ciclo 160-200 dias. Manejo intensivo de pragas (bicudo). MT e BA principais produtores.',
  },
  {
    cropId: 'girassol',
    name: 'Girassol',
    source: 'EMBRAPA',
    icon: '🌻',
    suitability: 'bom',
    plantingWindow: 'Fevereiro a Março (safrinha)',
    expectedYield: '1.500 - 2.500 kg/ha',
    notes: 'Óleo de alta qualidade. Rotação com soja. Ciclo 90-130 dias.',
  },
  {
    cropId: 'amendoim',
    name: 'Amendoim',
    source: 'EMBRAPA',
    icon: '🥜',
    suitability: 'bom',
    plantingWindow: 'Outubro a Novembro',
    expectedYield: '4.000 - 6.000 kg/ha (em casca)',
    notes: 'SP principal produtor. Renovação de canaviais. Ciclo 120-140 dias.',
  },
  {
    cropId: 'canola',
    name: 'Canola',
    source: 'EMBRAPA',
    icon: '🌼',
    suitability: 'moderado',
    plantingWindow: 'Abril a Junho',
    expectedYield: '1.500 - 2.500 kg/ha',
    notes: 'Óleo de alta qualidade. Cultura de inverno. Sul do Brasil. Rotação com trigo.',
  },
  {
    cropId: 'mamona',
    name: 'Mamona',
    source: 'EMBRAPA',
    icon: '🌿',
    suitability: 'moderado',
    plantingWindow: 'Outubro a Dezembro',
    expectedYield: '1.000 - 2.000 kg/ha',
    notes: 'Óleo para biodiesel e indústria. Tolerante à seca. Nordeste e Centro-Oeste.',
  },
  // ========================================
  // AGC: CANA E CULTURAS PERENES
  // ========================================
  {
    cropId: 'cana',
    name: 'Cana-de-açúcar',
    source: 'EMBRAPA',
    icon: '🎋',
    suitability: 'excelente',
    plantingWindow: 'Janeiro a Abril (ano todo)',
    expectedYield: '80 - 120 t/ha',
    notes: 'Ciclo 12-18 meses. 5-6 cortes/ciclo. Etanol e açúcar. SP maior produtor.',
  },
  {
    cropId: 'cafe',
    name: 'Café Arábica',
    source: 'EMBRAPA',
    icon: '☕',
    suitability: 'excelente',
    plantingWindow: 'Outubro a Março (implantação)',
    expectedYield: '30 - 60 sc/ha',
    notes: 'Cultura perene 20+ anos. Florada Set-Nov. Colheita Mai-Set. MG, SP, ES principais.',
  },
  // ========================================
  // AGC: TUBÉRCULOS E RAÍZES
  // ========================================
  {
    cropId: 'mandioca',
    name: 'Mandioca',
    source: 'EMBRAPA',
    icon: '🥔',
    suitability: 'bom',
    plantingWindow: 'Setembro a Novembro',
    expectedYield: '20 - 35 t/ha',
    notes: 'Ciclo 8-24 meses. Fécula e mesa. Tolerante a solos pobres. Todo Brasil.',
  },
  {
    cropId: 'batata',
    name: 'Batata',
    source: 'EMBRAPA',
    icon: '🥔',
    suitability: 'moderado',
    plantingWindow: 'Fev-Mar (seca) ou Ago-Set (águas)',
    expectedYield: '25 - 45 t/ha',
    notes: 'Alta tecnologia. Ciclo 90-120 dias. Irrigação necessária. MG e SP principais.',
  },
  // ========================================
  // AGC: PLANTAS DE COBERTURA
  // ========================================
  {
    cropId: 'crotalaria',
    name: 'Crotalária',
    source: 'EMBRAPA',
    icon: '🌿',
    suitability: 'excelente',
    plantingWindow: 'Outubro a Fevereiro',
    expectedYield: '8 - 15 t/ha (massa seca)',
    notes: 'Fixação de N (150-200 kg N/ha). Controle de nematoides. 60-90 dias até incorporação.',
  },
  {
    cropId: 'nabo_forrageiro',
    name: 'Nabo Forrageiro',
    source: 'EMBRAPA',
    icon: '🌿',
    suitability: 'bom',
    plantingWindow: 'Março a Maio',
    expectedYield: '4 - 8 t/ha (massa seca)',
    notes: 'Raiz pivotante descompacta solo. Reciclagem de nutrientes. 60-90 dias.',
  },
  {
    cropId: 'brachiaria',
    name: 'Braquiária',
    source: 'EMBRAPA',
    icon: '🌿',
    suitability: 'excelente',
    plantingWindow: 'Outubro a Dezembro',
    expectedYield: '10 - 20 t/ha (massa seca)',
    notes: 'Integração lavoura-pecuária. Cobertura permanente. Consórcio com milho.',
  },
  // ========================================
  // AGC: OUTROS (Other/Custom crops)
  // ========================================
  {
    cropId: 'outros',
    name: 'Outros / Outra Cultura',
    source: 'EMBRAPA',
    icon: '🌿',
    suitability: 'bom',
    plantingWindow: 'Consultar calendário local',
    expectedYield: 'Variável conforme cultura',
    notes: 'Selecione esta opção para culturas não listadas. O manejo será personalizado conforme necessário.',
  },
]

/**
 * AGC: Get crop recommendations based on current season
 * AGC: Returns all crops sorted by suitability for current planting window
 */
const getMockCropRecommendations = (): CropRecommendation[] => {
  // AGC: Get current month for season-based sorting
  const currentMonth = new Date().getMonth() + 1 // AGC: 1-12
  
  // AGC: Determine season
  const isSummerSeason = currentMonth >= 9 || currentMonth <= 3 // Sep-Mar
  const isWinterSeason = currentMonth >= 4 && currentMonth <= 8 // Apr-Aug
  
  // AGC: Create a copy and adjust suitability based on season
  const recommendations = ALL_CROP_RECOMMENDATIONS.map(crop => {
    let adjustedSuitability = crop.suitability
    
    // AGC: Summer crops (planted Sep-Mar)
    const summerCrops = ['soja', 'milho', 'arroz', 'feijao', 'feijao_carioca', 'feijao_preto', 
                        'algodao', 'amendoim', 'cana', 'cafe', 'mandioca', 'crotalaria', 'brachiaria']
    
    // AGC: Winter/Safrinha crops (planted Mar-Aug)
    const winterCrops = ['trigo', 'aveia', 'cevada', 'centeio', 'triticale', 'canola', 
                        'nabo_forrageiro', 'ervilhaca']
    
    // AGC: Safrinha crops (planted Jan-Mar)
    const safrinhaCrops = ['milho_safrinha', 'sorgo', 'girassol', 'milheto']
    
    if (isSummerSeason) {
      if (winterCrops.includes(crop.cropId)) {
        adjustedSuitability = 'moderado'
      }
    } else if (isWinterSeason) {
      if (summerCrops.includes(crop.cropId) && !['cafe', 'cana'].includes(crop.cropId)) {
        adjustedSuitability = 'moderado'
      }
      if (winterCrops.includes(crop.cropId)) {
        adjustedSuitability = 'excelente'
      }
    }
    
    // AGC: Safrinha window (Jan-Mar)
    if (currentMonth >= 1 && currentMonth <= 3) {
      if (safrinhaCrops.includes(crop.cropId)) {
        adjustedSuitability = 'excelente'
      }
    }
    
    return { ...crop, suitability: adjustedSuitability }
  })
  
  // AGC: Sort by suitability (excelente > bom > moderado)
  // AGC: "Outros" always appears at the end
  const suitabilityOrder: Record<string, number> = { excelente: 0, bom: 1, moderado: 2 }
  recommendations.sort((a, b) => {
    // AGC: "Outros" always goes to the end
    if (a.cropId === 'outros') return 1
    if (b.cropId === 'outros') return -1
    
    // AGC: Sort by suitability (best first)
    const orderA = suitabilityOrder[a.suitability] ?? 3
    const orderB = suitabilityOrder[b.suitability] ?? 3
    return orderA - orderB
  })
  
  return recommendations
}

/**
 * AGC: Suitability badge color
 */
const getSuitabilityColor = (suitability: string): string => {
  switch (suitability) {
    case 'excelente':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200'
    case 'bom':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'moderado':
      return 'bg-amber-100 text-amber-700 border-amber-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

/**
 * AGC: CropSuggestions - Crop selection screen
 * AGC: [CROP_REC] Now uses geo + climate aware recommendations
 */
export const CropSuggestions: React.FC<CropSuggestionsProps> = ({
  plotData,
  onNavigate,
  onBack,
  voicePrefillCrop,
  wizardMode,
  wizardStep
}) => {
  const { t } = useTranslation()
  
  // AGC: [CROP_REC] Check if we have valid coordinates for API recommendations
  const hasCoordinates = plotData.coordinates && plotData.coordinates.length >= 3

  // AGC: [REC_MANEJO] Toggle sequeiro/irrigado — default sequeiro, persistido em localStorage
  const [manejo, setManejo] = useState<Manejo>(() => {
    if (typeof window === 'undefined') return 'sequeiro'
    const stored = window.localStorage.getItem('droidfarm.recommendations.manejo')
    return stored === 'irrigado' ? 'irrigado' : 'sequeiro'
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('droidfarm.recommendations.manejo', manejo)
    }
  }, [manejo])

  // AGC: [REC_MONTH] Planting month selector — default today, user can change to see future windows
  const [plantingMonth, setPlantingMonth] = useState<number>(new Date().getMonth() + 1)
  const [showMonthPicker, setShowMonthPicker] = useState(false)
  // AGC: [AR] AR-selected planting month — null means "use the ORA window-start default" from the backend.
  const [arMonth, setArMonth] = useState<number | null>(null)
  const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  const currentMonth = new Date().getMonth() + 1
  const isCurrentMonth = plantingMonth === currentMonth

  // AGC: [CROP_REC] Fetch geo + climate aware recommendations from API
  // AGC: [AUDIT_FIX] Use plotData.id (not name) so audit events link to the correct lot
  const {
    recommendations: apiRecommendations,
    locationClimate,
    targetMonth,
    yearsAnalyzed,
    isLoading: isLoadingAPI,
    error: apiError,
    refetch: refetchRecommendations,
    geographicContext,
    excludedCrops,
    provider,
    zone,
    incomplete,
    effectivePlantingMonth,
    defaultPlantingMonth,
    coveredCount,
    catalogTotal,
  } = useCropRecommendations({
    lotId: plotData.id || plotData.name, // AGC: [AUDIT_FIX] Prefer ID for audit consistency
    fieldId: plotData.fieldId, // AGC: [AUDIT_FIX] Include fieldId for proper audit logging
    coordinates: hasCoordinates ? plotData.coordinates as [number, number][] : null,
    enabled: hasCoordinates,
    manejo, // AGC: [REC_MANEJO] Sequeiro or irrigado from toggle
    plantingMonth: arMonth ?? plantingMonth, // AGC: [REC_MONTH][AR] AR uses arMonth (ORA window-start default); BR keeps plantingMonth
  })

  // AGC: [AR] Default the AR planting month to the ORA window start (not "today"), once per lot.
  useEffect(() => {
    if (provider === 'argentina' && arMonth === null && typeof defaultPlantingMonth === 'number') {
      setArMonth(defaultPlantingMonth)
    }
  }, [provider, defaultPlantingMonth, arMonth])
  
  // AGC: [CROP_REC] Use API recommendations if available, otherwise fall back to mock
  const useApiData = apiRecommendations && apiRecommendations.length > 0
  
  // AGC: [CROP_REC] Convert API recommendations to component format
  const recommendations: CropRecommendation[] = useMemo(() => {
    if (useApiData) {
      // AGC: [AR][W#2] AR recs have a different shape than BR — branch the mapping by provider so we
      // AGC: carry the real AR fields (score/projected_yield_kg_ha/risk_flags/planting_window) instead
      // AGC: of reading BR-only fields that are undefined on AR (data-conversion.md: no placeholder strip).
      if (provider === 'argentina') {
        console.log('🌱 AGC: [CROP_REC][AR] Using AR API recommendations')
        return apiRecommendations.map(rec => {
          const ar = rec as unknown as import('../../types/recommendation').ArgentinaRecommendation
          // AGC: not `window` — avoid shadowing the global window object.
          const plantingWin = ar.planting_window
            ? `${ar.planting_window.recommended_start} – ${ar.planting_window.recommended_end}`
            : ''
          return {
            cropId: ar.cropId as CropType,
            name: ar.name || AR_CROP_NAMES[ar.cropId]?.name || ar.cropId,
            source: 'INTA' as const,
            icon: ar.icon || AR_CROP_NAMES[ar.cropId]?.icon || '🌱',
            // AGC: AR degraded entries have suitability=null; coalesce for the non-null local type.
            // AGC: This array is only used to select AR crops by cropId (BR cards are provider-gated off).
            suitability: ar.suitability ?? 'moderado',
            plantingWindow: plantingWin,
            expectedYield: typeof ar.projected_yield_kg_ha === 'number'
              ? `${ar.projected_yield_kg_ha} kg/ha`
              : '',
            notes: '',
            suitabilityScore: typeof ar.score === 'number' ? ar.score : 0,
            risks: Array.isArray(ar.risk_flags) ? ar.risk_flags.map(f => f.type) : undefined,
          }
        })
      }
      console.log('🌱 AGC: [CROP_REC] Using API recommendations')
      return apiRecommendations.map(rec => ({
        cropId: rec.cropId as CropType,
        name: rec.name,
        source: rec.source as 'EMBRAPA' | 'INTA',
        icon: rec.icon,
        suitability: rec.suitability,
        plantingWindow: rec.plantingWindow,
        // AGC: [FIX] Handle null/NaN projectedYield safely
        expectedYield: rec.projectedYield != null && !isNaN(rec.projectedYield)
          ? `${rec.projectedYield.toFixed(1)} tn/ha (ajustado pelo clima)`
          : 'Estimativa pendente',
        notes: rec.climateSummary,
        // AGC: [CROP_REC] Extra fields from API
        suitabilityScore: rec.suitabilityScore ?? 0,
        climateScore: rec.climateScore ?? 0,
        seasonScore: rec.seasonScore ?? 0,
        risks: rec.risks,
        scores: rec.scores,
      }))
    }

    console.log('📋 AGC: [CROP_REC] Using mock recommendations (no API data)')
    return getMockCropRecommendations()
  }, [useApiData, apiRecommendations, provider])
  
  // AGC: Selected crop state
  const [selectedCrop, setSelectedCrop] = useState<CropRecommendation | null>(
    recommendations[0] || null
  )
  
  // AGC: Auto-select voice pre-filled crop when recommendations are loaded
  useEffect(() => {
    if (voicePrefillCrop && recommendations.length > 0) {
      const matchingCrop = recommendations.find(
        (rec) => rec.cropId.toLowerCase() === voicePrefillCrop.toLowerCase() ||
                 rec.name.toLowerCase() === voicePrefillCrop.toLowerCase()
      )
      if (matchingCrop) {
        console.log('🎤 AGC: Auto-selecting voice pre-filled crop:', matchingCrop.name)
        setSelectedCrop(matchingCrop)
      }
    }
  }, [voicePrefillCrop, recommendations])

  // AGC: [REC_R5] Auto-select first recommendation when recs load and nothing is selected.
  useEffect(() => {
    if (selectedCrop) return
    if (voicePrefillCrop) return
    if (recommendations.length === 0) return
    setSelectedCrop(recommendations[0])
  }, [recommendations, voicePrefillCrop, selectedCrop])

  // AGC: Editable lot name state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState(plotData.name)
  const [currentPlotData, setCurrentPlotData] = useState(plotData)
  
  // AGC: [LOADING_UX] Rotating loading message state (Canva-style)
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  
  // AGC: [LOADING_UX] Rotate through loading messages every 2 seconds
  useEffect(() => {
    if (!isLoadingAPI) {
      setLoadingMessageIndex(0) // AGC: Reset when loading completes
      return
    }
    
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % LOADING_ICONS.length)
    }, 2000)
    
    return () => clearInterval(interval)
  }, [isLoadingAPI])
  
  // AGC: Handle name edit save
  const handleSaveName = () => {
    if (editedName.trim()) {
      setCurrentPlotData(prev => ({ ...prev, name: editedName.trim() }))
      setIsEditingName(false)
    }
  }
  
  // AGC: Handle name edit cancel
  const handleCancelEdit = () => {
    setEditedName(currentPlotData.name)
    setIsEditingName(false)
  }
  
  // AGC: Calculate perimeter if we have coordinates
  const perimeter = useMemo(() => {
    if (currentPlotData.metrics?.perimeterMeters && currentPlotData.metrics.perimeterMeters > 0) {
      return currentPlotData.metrics.perimeterMeters
    }
    if (currentPlotData.coordinates && currentPlotData.coordinates.length >= 3) {
      return calculatePerimeter(currentPlotData.coordinates)
    }
    return 0
  }, [currentPlotData.coordinates, currentPlotData.metrics?.perimeterMeters])

  // AGC: Prefer canonical area from metrics when coming from AI detection
  const areaHectares = useMemo(() => {
    if (typeof currentPlotData.metrics?.areaHectares === 'number' && currentPlotData.metrics.areaHectares > 0) {
      return currentPlotData.metrics.areaHectares
    }
    return currentPlotData.area || 0
  }, [currentPlotData.area, currentPlotData.metrics?.areaHectares])

  // AGC: [UNIFIED_FLOW] Handle continue to task planning
  const handleContinue = () => {
    if (!selectedCrop) {
      alert(t('fields.selectCropToContinue'))
      return
    }
    
    // AGC: [UNIFIED_FLOW] Preserve ALL plotData fields when navigating
    // AGC: This includes metrics, detectionMethod, centroid, etc. from FinalizarLote
    // AGC: [AR] Resolve the lot country so task planning can show a per-country "no auto plan" message.
    const lotCountry = provider === 'argentina' ? 'AR' : (geographicContext?.country || undefined)
    const updatedPlotData: PlotFormData = {
      ...currentPlotData,  // AGC: Use currentPlotData with edited name and all original fields
      cropType: selectedCrop.cropId,
      country: lotCountry,
    }
    
    console.log('🔄 AGC: [UNIFIED_FLOW] CropSuggestions → field-task-planning')
    console.log('   Lot ID:', updatedPlotData.id || '(not yet saved)')
    console.log('   Name:', updatedPlotData.name)
    console.log('   Crop:', updatedPlotData.cropType)
    console.log('   Detection method:', updatedPlotData.detectionMethod || 'polygon')
    console.log('   Metrics:', updatedPlotData.metrics ? 'present' : 'not present')
    
    // AGC: Navigate to task planning (lot will be saved there if not already saved)
    onNavigate('field-task-planning', { plotData: updatedPlotData })
  }

  // AGC: Alias for backwards compatibility
  const handleSave = handleContinue
  const handleGoToPlanning = handleContinue

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-40">
      {/* AGC: Top Bar */}
      <TopBar
        title={t('fields.cropSuggestion')}
        subtitle={wizardMode ? t('fields.wizardLotStep3of5') : t('fields.step2of3')}
        onBack={onBack}
      />

      {/* AGC: [UNIFIED_FLOW] Progress indicator - Step 2 of 3 or Wizard Phase 2 */}
      {wizardMode ? (
        <WizardLotProgressBar currentStep={wizardStep || 3} />
      ) : (
        <div className="px-4 py-3">
          <div className="flex gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
            <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
            <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-slate-700" />
          </div>
        </div>
      )}

      {/* AGC: Field Summary with editable name */}
      <div className="px-4 py-2">
        <GlassCard className="bg-emerald-50 border-emerald-200">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-600" />
              {isEditingName ? (
                // AGC: Edit mode - show input with save/cancel buttons
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm font-medium text-emerald-800 bg-white border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName()
                      if (e.key === 'Escape') handleCancelEdit()
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    className="p-1 text-emerald-600 hover:text-emerald-800"
                    title="Salvar"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Cancelar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                // AGC: View mode - show name with pencil icon
                <div className="flex items-center gap-2 flex-1">
                  <span className="font-medium text-emerald-800">{currentPlotData.name}</span>
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="p-1 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded"
                    title="Editar nome"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-4 text-sm text-emerald-700">
              <div className="flex items-center gap-1">
                <Ruler className="w-3 h-3" />
                <span>Área: {formatAreaHa(areaHectares)}</span>
              </div>
              {perimeter > 0 && (
                <div className="flex items-center gap-1">
                  <span>Perímetro: {formatPerimeterM(perimeter)}</span>
                </div>
              )}
            </div>
          </div>
        </GlassCard>
      </div>

      {/* AGC: [CROP_REC] Climate Info Card (when API data available) */}
      {useApiData && locationClimate && (
        <div className="px-4 py-2">
          <GlassCard className="bg-blue-50 border-blue-200">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ThermometerSun className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800 text-sm">{t('fields.climaticProfile')}</span>
                </div>
                <button
                  onClick={() => refetchRecommendations()}
                  disabled={isLoadingAPI}
                  className="p-1 text-blue-500 hover:text-blue-700 disabled:opacity-50"
                  title="Atualizar análise"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingAPI ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <CloudRain className="w-3 h-3 text-blue-500" />
                  <span className="text-blue-700">
                    Precipitação: {locationClimate.avgPrecipitation?.toFixed(0) || '---'} mm
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <ThermometerSun className="w-3 h-3 text-orange-500" />
                  <span className="text-blue-700">
                    Temp. média: {locationClimate.avgTemperature?.toFixed(1) || '---'}°C
                  </span>
                </div>
              </div>
              <p className="text-xs text-blue-600">
                Análise baseada em {yearsAnalyzed} anos de dados históricos para mês {targetMonth}
              </p>
            </div>
          </GlassCard>
        </div>
      )}

          {/* AGC: [ZARC] Geographic context banner with month picker */}
          {geographicContext?.filterApplied && (
            <div className="px-4 py-2">
              <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-base font-semibold text-green-800 dark:text-green-300">
                    {t('fields.geographic.recommendationsFor', {
                      municipio: geographicContext.municipio,
                      uf: geographicContext.UF,
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <button
                      onClick={() => setShowMonthPicker(!showMonthPicker)}
                      className="text-sm font-medium text-green-700 dark:text-green-300 underline underline-offset-2 decoration-dashed"
                    >
                      {isCurrentMonth
                        ? t('fields.geographic.plantingToday')
                        : t('fields.geographic.plantingMonth', { month: MONTH_NAMES[plantingMonth - 1] })}
                    </button>
                    {!isCurrentMonth && (
                      <button
                        onClick={() => setPlantingMonth(currentMonth)}
                        className="ml-1 text-xs text-green-500 hover:text-green-700"
                      >
                        (hoje)
                      </button>
                    )}
                  </div>
                  {excludedCrops.length > 0 && (
                    <span className="text-xs text-green-600 dark:text-green-400">
                      {t('fields.geographic.excludedByZarc', { count: excludedCrops.length })}
                    </span>
                  )}
                </div>
                {showMonthPicker && (
                  <div className="grid grid-cols-4 gap-1.5 mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                    {MONTH_NAMES.map((name, i) => (
                      <button
                        key={i}
                        onClick={() => { setPlantingMonth(i + 1); setShowMonthPicker(false) }}
                        className={`px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          plantingMonth === i + 1
                            ? 'bg-green-600 text-white'
                            : i + 1 === currentMonth
                              ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-green-100'
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
      {/* AGC: [AR] Argentina engine zone panel (replaces the no-ZARC banner for AR lots). */}
      {provider === 'argentina' && zone && (
        <div className="px-4 py-2">
          <div className="px-4 py-3 bg-sky-50 dark:bg-sky-900/20 rounded-xl border border-sky-200 dark:border-sky-800">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-sky-600 dark:text-sky-400" />
              <span className="text-base font-semibold text-sky-800 dark:text-sky-300">
                {t('fields.geographic.arZone', { zone: zone.name })}
              </span>
            </div>
            {typeof coveredCount === 'number' && typeof catalogTotal === 'number' && (
              <p className="mt-1 text-xs text-sky-700 dark:text-sky-300">
                {t('fields.recommendations.coveredCount', { covered: coveredCount, total: catalogTotal, zone: zone.name })}
              </p>
            )}
            {incomplete && (
              <span className="mt-1 inline-flex items-center gap-1 text-xs text-amber-600">
                {t('fields.geographic.arIncomplete')}
                {/* AGC: [AR][#2] (?) tooltip listing which factors are pending and why (NO INVENTAR). */}
                <span
                  className="cursor-help rounded-full border border-amber-400 text-amber-600 w-3.5 h-3.5 inline-flex items-center justify-center text-[9px] leading-none font-bold"
                  title={t('fields.geographic.arIncompleteHelp')}
                  aria-label={t('fields.geographic.arIncompleteHelp')}
                >?</span>
              </span>
            )}
            {/* AGC: [AR] sowing-month picker (default = ORA window start; changing it re-scores) */}
            <div className="mt-2 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-sky-600 dark:text-sky-400" />
              <button onClick={() => setShowMonthPicker(!showMonthPicker)}
                className="text-sm font-medium text-sky-700 dark:text-sky-300 underline underline-offset-2 decoration-dashed">
                {t('fields.geographic.arSowMonth', { month: MONTH_NAMES[((((arMonth ?? effectivePlantingMonth ?? 1) - 1) % 12) + 12) % 12] })}
              </button>
            </div>
            {showMonthPicker && (
              <div className="grid grid-cols-4 gap-1.5 mt-2">
                {MONTH_NAMES.map((name, i) => {
                  const active = (arMonth ?? effectivePlantingMonth) === i + 1
                  return (
                    <button key={i} onClick={() => { setArMonth(i + 1); setShowMonthPicker(false) }}
                      className={`px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${active ? 'bg-sky-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-sky-100'}`}>
                      {name}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {/* AGC: [AR] Lot outside ORA coverage — explain and still allow manual crop selection. */}
      {provider === 'argentina' && !zone && (
        <div className="px-4 py-2">
          <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-800 dark:text-amber-300">
                {t('fields.recommendations.outsideZone')}
              </span>
            </div>
          </div>
        </div>
      )}
          {provider !== 'argentina' && geographicContext?.country && geographicContext.country !== 'BR' && !geographicContext.filterApplied && (
            <div className="px-4 py-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                <MapPin className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                <span className="text-sm text-yellow-700 dark:text-yellow-300">
                  {t('fields.geographic.noZarcOutsideBrazil')}
                </span>
              </div>
            </div>
          )}

      {/* AGC: [LOADING_UX] Canva-style Loading Screen */}
      {isLoadingAPI && (
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-16">
          {/* AGC: Animated progress circle */}
          <div className="relative w-32 h-32 mb-8">
            {/* Background circle */}
            <svg className="w-32 h-32 transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#E5E7EB"
                strokeWidth="8"
                fill="none"
                className="dark:stroke-slate-700"
              />
              {/* Animated progress arc */}
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#0B5F4A"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="352"
                strokeDashoffset={352 - (352 * ((loadingMessageIndex + 1) / LOADING_ICONS.length))}
                className="transition-all duration-500 ease-out"
              />
            </svg>
            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-4xl animate-pulse">
                {LOADING_ICONS[loadingMessageIndex]}
              </span>
            </div>
          </div>
          
          {/* AGC: Status message with fade animation */}
          <div className="text-center space-y-3 min-h-[80px]">
            <p className="text-lg font-medium text-gray-800 dark:text-gray-200 transition-all duration-300">
              {t(`fields.cropLoadingStep${loadingMessageIndex + 1}`)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('fields.cropLoadingYears', { years: yearsAnalyzed || 5 })}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Localização: {plotData.centroid?.lat?.toFixed(4) || '--'}, {plotData.centroid?.lng?.toFixed(4) || '--'}
            </p>
          </div>
          
          {/* AGC: Step indicator dots */}
          <div className="flex gap-2 mt-8">
            {LOADING_ICONS.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx <= loadingMessageIndex 
                    ? 'bg-emerald-500 scale-110' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* AGC: Only show recommendations when NOT loading */}
      {!isLoadingAPI && (
        <>
          {/* AGC: [CROP_REC] API Error Warning */}
          {apiError && (
            <div className="px-4 py-2">
              <GlassCard className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{t('fields.usingStandardRecommendations')}</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      {t('fields.analysisFailedUsingDefaults')}
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>
          )}

          {/* AGC: Recommendations Header */}
          <div className="px-4 py-4">
            <div className="flex items-center gap-2 mb-2">
              <Leaf className="w-5 h-5 text-[#0B5F4A]" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('fields.recommendedCrops')}
              </h3>
              {useApiData && (
                <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 rounded-full">
                  Geo + Clima
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {useApiData
                ? t('fields.recommendationsBasedOnLocation')
                : t('fields.recommendationsBasedOnRegion')
              }
            </p>
          </div>

          {/* AGC: [REC_MANEJO][AR][RIEGO] Argentina: single "¿El lote tiene riego?" switch (on→irrigado,
              off→sequeiro). It now actually drives the AR engine (water balance + yield). Brazil keeps the
              original Secano/Irrigado two-button (ZARC), unchanged. */}
          {provider === 'argentina' ? (
            <div className="flex items-center justify-between gap-3 px-4 mb-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('fields.recommendations.hasIrrigation')}</span>
              <button
                type="button"
                role="switch"
                aria-checked={manejo === 'irrigado'}
                onClick={() => setManejo(manejo === 'irrigado' ? 'sequeiro' : 'irrigado')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${manejo === 'irrigado' ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${manejo === 'irrigado' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 mb-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.recommendations.manejo.label')}</span>
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setManejo('sequeiro')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    manejo === 'sequeiro' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  aria-pressed={manejo === 'sequeiro'}
                >
                  {t('admin.recommendations.manejo.sequeiro')}
                </button>
                <button
                  type="button"
                  onClick={() => setManejo('irrigado')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                    manejo === 'irrigado' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  aria-pressed={manejo === 'irrigado'}
                >
                  {t('admin.recommendations.manejo.irrigado')}
                </button>
              </div>
            </div>
          )}

          {/* AGC: Crop Recommendations List */}
          <div className="px-4 space-y-3 pb-4">
        {provider !== 'argentina' && recommendations.map((crop) => (
          <button
            key={crop.cropId}
            onClick={() => setSelectedCrop(crop)}
            className="w-full text-left"
          >
            <GlassCard
              className={`border-2 transition-all duration-200 ${
                selectedCrop?.cropId === crop.cropId
                  ? 'border-[#0B5F4A] bg-emerald-50'
                  : 'border-transparent hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* AGC: Selection indicator */}
                <div className={`
                  w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1
                  ${selectedCrop?.cropId === crop.cropId
                    ? 'border-[#0B5F4A] bg-[#0B5F4A]'
                    : 'border-gray-300'
                  }
                `}>
                  {selectedCrop?.cropId === crop.cropId && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </div>

                {/* AGC: Crop icon */}
                <span className="text-3xl flex-shrink-0">{crop.icon}</span>

                {/* AGC: Crop details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{crop.name}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${getSuitabilityColor(crop.suitability)}`}>
                      {t('fields.suitability' + crop.suitability.charAt(0).toUpperCase() + crop.suitability.slice(1))}
                    </span>
                    {/* AGC: [CROP_REC] Show score if available */}
                    {crop.suitabilityScore != null && !isNaN(crop.suitabilityScore) && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        {crop.suitabilityScore.toFixed(0)}%
                      </span>
                    )}
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {crop.source}
                    </span>
                  </div>

                  {/* AGC: [CROP_REC] Show detailed scores when from API */}
                  {crop.scores && (
                    <div className="mt-2 flex gap-2 text-xs">
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded" title="Precipitação">
                        💧 {crop.scores.precipitation != null && !isNaN(crop.scores.precipitation) ? crop.scores.precipitation.toFixed(0) : '--'}
                      </span>
                      <span className="px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded" title="Temperatura">
                        🌡️ {crop.scores.temperature != null && !isNaN(crop.scores.temperature) ? crop.scores.temperature.toFixed(0) : '--'}
                      </span>
                      <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded" title="Época">
                        📅 {crop.scores.season != null && !isNaN(crop.scores.season) ? crop.scores.season.toFixed(0) : '--'}
                      </span>
                    </div>
                  )}

                  <div className="mt-2 text-sm text-gray-600 space-y-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      <span>{t('fields.planting') + ': '}{crop.plantingWindow}</span>
                    </div>
                    <div>
                      <span>{t('fields.expectedYield') + ': '}{crop.expectedYield}</span>
                    </div>
                  </div>

                  {selectedCrop?.cropId === crop.cropId && (
                    <>
                      <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-blue-700">{crop.notes}</p>
                        </div>
                      </div>
                      {/* AGC: [CROP_REC] Show risks if available */}
                      {crop.risks && crop.risks.length > 0 && (
                        <div className="mt-2 p-2 bg-amber-50 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-amber-700">
                              <p className="font-medium mb-1">{t('fields.identifiedRisks')}</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                {crop.risks.slice(0, 3).map((risk, i) => (
                                  <li key={i}>{risk}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </GlassCard>
          </button>
        ))}
      </div>
      {/* AGC: [AR] Argentina recommendation list — real recs + degraded (selectable, no rec) entries. */}
      {/* AGC: [AR][BUGFIX] Iterate the RAW apiRecommendations (full AR shape), NOT the BR-remapped
          `recommendations` array — the useMemo above strips data_status/score/recommended/yields/risk_flags,
          which made `isDegraded` true for every crop ("sin recomendación" for all). Same order/length as
          `recommendations`; selection stays keyed by cropId so auto-select + highlighting are unaffected. */}
      {provider === 'argentina' && (
        <div className="px-4 py-2 space-y-2">
          {apiRecommendations.map((crop) => {
            const rec = crop as unknown as import('../../types/recommendation').ArgentinaRecommendation
            const display = { name: rec.name || AR_CROP_NAMES[rec.cropId]?.name || rec.cropId, icon: rec.icon || AR_CROP_NAMES[rec.cropId]?.icon || '🌱' }
            const isDegraded = rec.data_status !== 'ok'
            const wrongSeason = Array.isArray(rec.risk_flags) && rec.risk_flags.some((f: { type: string }) => f.type === 'wrong_season')
            // AGC: [AR][BUGFIX] `crop` is the imported (API) CropRecommendation shape; selectedCrop state
            // AGC: holds the LOCAL shape. Select the parallel local-shape entry by cropId (same order as
            // AGC: apiRecommendations) so the type stays consistent with auto-select and handleContinue.
            return (
              <button key={rec.cropId} onClick={() => setSelectedCrop(recommendations.find((r) => r.cropId === rec.cropId) ?? null)} className="w-full text-left">
                <GlassCard className={`border-2 transition-all duration-200 ${selectedCrop?.cropId === rec.cropId ? 'border-[#0B5F4A] bg-emerald-50' : 'border-transparent hover:border-gray-300'}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{display.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 dark:text-gray-100">{display.name}</span>
                        {isDegraded
                          ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400">{t('fields.recommendations.noRecommendation')}</span>
                          : (rec.recommended
                              ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{t('fields.recommendations.recommended')}</span>
                              : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400">{t('fields.recommendations.notRecommended')}</span>)}
                      </div>
                      {!isDegraded && (
                        <>
                          <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {rec.suitability
                              ? t('fields.suitability' + rec.suitability.charAt(0).toUpperCase() + rec.suitability.slice(1))
                              : ''}
                            {typeof rec.score === 'number' ? ` · ${t('fields.recommendations.score')}: ${rec.score}` : ''}
                          </div>
                          <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                            {typeof rec.projected_yield_kg_ha === 'number'
                              ? (typeof rec.projected_total_kg === 'number'
                                  ? t('fields.recommendations.arYieldTotal', { yield: rec.projected_yield_kg_ha, total: rec.projected_total_kg })
                                  : t('fields.recommendations.arYield', { yield: rec.projected_yield_kg_ha }))
                              : t('fields.recommendations.arYieldUnknown')}
                            {wrongSeason && <span className="ml-2 text-amber-600">{t('fields.recommendations.wrongSeason')}</span>}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </GlassCard>
              </button>
            )
          })}
        </div>
      )}

          {/* AGC: [CROP_REC] Data source info */}
          <div className="px-4 pb-4">
            <GlassCard className={useApiData ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700' : 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600'}>
              <div className="flex gap-2 items-start">
                <Info className={`w-4 h-4 flex-shrink-0 mt-0.5 ${useApiData ? 'text-emerald-500' : 'text-gray-500 dark:text-gray-400'}`} />
                <p className={`text-xs ${useApiData ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  {useApiData ? (
                    <>
                      <strong>🌱 {t('fields.activeClimateAnalysis')}:</strong> {t('fields.activeClimateAnalysisDesc', { years: yearsAnalyzed })}
                    </>
                  ) : (
                    <>
                      <strong>{t('fields.dataSources')}</strong> {hasCoordinates
                        ? t('fields.dataSourcesConnecting')
                        : t('fields.dataSourcesAddCoordinates')
                      }
                    </>
                  )}
                </p>
              </div>
            </GlassCard>
          </div>
        </>
      )}

      {/* AGC: Action buttons - ALWAYS visible, even during loading */}
      <div
        className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 space-y-2 z-[90]"
        style={{ paddingBottom: 'calc(max(env(safe-area-inset-bottom), 10px) + 10px)' }}
      >
        {/* AGC: Show loading message when fetching recommendations */}
        {isLoadingAPI && (
          <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-blue-700 dark:text-blue-300">
              {t('fields.analyzingClimate') || 'Analisando clima do lote...'}
            </span>
          </div>
        )}
        
        {/* AGC: Show selection reminder when not loading and no crop selected */}
        {!isLoadingAPI && !selectedCrop && (
          <div className="flex items-center justify-center gap-2 py-2 px-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-2">
            <span className="text-sm text-amber-700 dark:text-amber-300">
              👆 {t('fields.selectCropToContinue') || 'Selecione uma cultura acima para continuar'}
            </span>
          </div>
        )}
        
        <div className="flex gap-3">
          <Button 
            variant="secondary" 
            onClick={handleSave}
            disabled={!selectedCrop || isLoadingAPI}
          >
            {t('common.save') || 'Salvar'}
          </Button>
          <Button 
            fullWidth
            onClick={handleGoToPlanning}
            disabled={!selectedCrop || isLoadingAPI}
          >
            {t('fields.goToPlanning') || 'Ir para planejamento'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CropSuggestions






